import React, { useState, useRef, KeyboardEvent } from 'react';
import { useChatContext } from './store/ChatContext';
import { UIMessage } from './types/chat.types';
import { IoSend } from 'react-icons/io5';

interface Props {
  onSend: (text: string) => void;
  isBlocked?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<Props> = ({
  onSend,
  isBlocked = false,
  placeholder = 'Type a message\u2026',
}) => {
  const { state, dispatch } = useChatContext();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDisabled = state.isStreaming || isBlocked || state.isLoadingHistory;

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || isDisabled) return;
    const userMsg: UIMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: trimmed,
      timestamp: new Date().toISOString(),
    };
    // Latch the previous turn's option lists as superseded BEFORE appending
    // the user message (the reducer marks the current last message).
    dispatch({ type: 'MARK_LAST_SUPERSEDED' });
    dispatch({ type: 'ADD_MESSAGE', message: userMsg });
    onSend(trimmed);
  };

  const handleSend = () => {
    submit(text);
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleChipClick = (value: string) => {
    // ADD_MESSAGE clears chips automatically on user-role messages, so we
    // don't need a separate CLEAR_SUGGESTED_REPLIES dispatch here.
    submit(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 96) + 'px';
    }
  };

  return (
    <>
      {state.suggestedReplies.length > 0 && (
        <div className="bot-chips">
          {state.suggestedReplies.map((chip, i) => (
            <button
              key={`${chip.label}-${i}`}
              className="bot-chips__btn"
              type="button"
              onClick={() => handleChipClick(chip.value)}
              disabled={isDisabled}
              title={chip.value}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
      <div className={`bot-input ${isDisabled ? 'bot-input--disabled' : ''}`}>
        <textarea
          ref={textareaRef}
          className="bot-input__textarea"
          placeholder={isBlocked ? 'Chat disabled' : placeholder}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          rows={1}
        />
        <button
          className="bot-input__send"
          onClick={handleSend}
          disabled={isDisabled || !text.trim()}
          aria-label="Send"
        >
          <IoSend size={18} />
        </button>
      </div>
    </>
  );
};

export default ChatInput;
